import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { activityApi } from '../utils/api';
import { getImageUrl } from '../config/imageConfig';
import './EventDetail.css';

// 默认活动图片
const defaultEventImg = require('../assets/xlhy/x4.png');

function EventDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [relatedEvents, setRelatedEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [registering, setRegistering] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [registrationData, setRegistrationData] = useState({
    name: '',
    phone: '',
    email: '',
    participants: 1,
    remarks: ''
  });

  useEffect(() => {
    fetchEventDetail();
    // 增加浏览量
    if (id) {
      activityApi.addView(id).catch(err => console.log('Failed to add view:', err));
    }
  }, [id]);

  const fetchEventDetail = async () => {
    try {
      setLoading(true);
      setError(null);

      // 获取活动详情
      const response = await activityApi.getDetail(id);
      
      if (response && response.code === 200 && response.data) {
        const eventData = response.data;
        setEvent(eventData);
        
        // 获取相关推荐活动
        try {
          const relatedResponse = await activityApi.getRelated(id, 3);
          if (relatedResponse && relatedResponse.code === 200 && relatedResponse.data) {
            setRelatedEvents(relatedResponse.data);
          }
        } catch (err) {
          console.log('Failed to fetch related events:', err);
        }
      } else {
        setError('活动不存在或已下线');
      }
    } catch (err) {
      console.error('获取活动详情失败:', err);
      setError('加载活动详情失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  const handleRegistration = async () => {
    if (!validateRegistrationForm()) return;
    
    try {
      setRegistering(true);
      const response = await activityApi.register(id, registrationData);
      
      if (response && response.code === 200) {
        alert('报名成功！我们会尽快与您联系。');
        setShowRegistrationForm(false);
        setRegistrationData({
          name: '',
          phone: '',
          email: '',
          participants: 1,
          remarks: ''
        });
      } else {
        alert(response.message || '报名失败，请稍后重试');
      }
    } catch (err) {
      console.error('报名失败:', err);
      alert('报名失败，请稍后重试');
    } finally {
      setRegistering(false);
    }
  };

  const validateRegistrationForm = () => {
    if (!registrationData.name.trim()) {
      alert('请输入姓名');
      return false;
    }
    if (!registrationData.phone.trim()) {
      alert('请输入手机号');
      return false;
    }
    if (!/^1[3-9]\d{9}$/.test(registrationData.phone)) {
      alert('请输入正确的手机号');
      return false;
    }
    if (registrationData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registrationData.email)) {
      alert('请输入正确的邮箱地址');
      return false;
    }
    return true;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '待定';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (startTime, endTime) => {
    if (!startTime) return '';
    if (!endTime) return startTime;
    return `${startTime} - ${endTime}`;
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'upcoming': { text: '即将开始', color: '#00a862' },
      'ongoing': { text: '进行中', color: '#ff6b35' },
      'ended': { text: '已结束', color: '#999' },
      'cancelled': { text: '已取消', color: '#dc3545' },
      'full': { text: '已满员', color: '#ffc107' }
    };
    const statusInfo = statusMap[status] || { text: '待定', color: '#666' };
    return (
      <span 
        className="event-status-badge" 
        style={{ backgroundColor: statusInfo.color }}
      >
        {statusInfo.text}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="event-detail-container">
        <div className="loading-spinner">加载中...</div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="event-detail-container">
        <div className="error-message">
          <h2>{error || '活动不存在'}</h2>
          <button onClick={() => navigate('/')} className="back-button">
            返回首页
          </button>
        </div>
      </div>
    );
  }

  // 处理活动图片
  const eventImage = getImageUrl(
    event.featuredImage || 
    event.coverImage || 
    event.image || 
    event.imageUrl
  ) || defaultEventImg;

  // 判断是否可以报名
  const canRegister = event.status === 'upcoming' && 
                      event.registrationEnabled !== false && 
                      (!event.maxParticipants || event.currentParticipants < event.maxParticipants);

  return (
    <div className="event-detail-container">
      {/* 顶部导航 */}
      <div className="event-breadcrumb">
        <Link to="/">首页</Link>
        <span className="separator">/</span>
        <Link to="/events">活动</Link>
        <span className="separator">/</span>
        <span>{event.title}</span>
      </div>

      {/* 活动主体内容 */}
      <div className="event-detail-wrapper">
        {/* 左侧主要内容 */}
        <div className="event-main-content">
          {/* 活动头部信息 */}
          <div className="event-header">
            <div className="event-header-info">
              <div className="event-tags">
                {event.category && (
                  <span className="event-category-tag">{event.category.name}</span>
                )}
                {getStatusBadge(event.status)}
                {event.tags && event.tags.map((tag, index) => (
                  <span key={index} className="event-tag">{tag}</span>
                ))}
              </div>
              <h1 className="event-title">{event.title}</h1>
              {event.subtitle && (
                <p className="event-subtitle">{event.subtitle}</p>
              )}
              <div className="event-meta">
                <span className="meta-item">
                  <i className="icon-eye"></i> {event.viewCount || 0} 次浏览
                </span>
                <span className="meta-item">
                  <i className="icon-user"></i> {event.currentParticipants || 0} 人已报名
                </span>
                {event.maxParticipants && (
                  <span className="meta-item">
                    <i className="icon-users"></i> 限 {event.maxParticipants} 人
                  </span>
                )}
              </div>
            </div>
            <div className="event-header-image">
              <img 
                src={eventImage} 
                alt={event.title}
                onError={(e) => { e.target.src = defaultEventImg; }}
              />
            </div>
          </div>

          {/* 活动详细信息 */}
          <div className="event-detail-sections">
            {/* 活动简介 */}
            {event.summary && (
              <section className="detail-section">
                <h2>活动简介</h2>
                <p>{event.summary}</p>
              </section>
            )}

            {/* 活动详情 */}
            {event.content && (
              <section className="detail-section">
                <h2>活动详情</h2>
                <div 
                  className="rich-content"
                  dangerouslySetInnerHTML={{ __html: event.content }}
                />
              </section>
            )}

            {/* 活动流程 */}
            {event.agenda && event.agenda.length > 0 && (
              <section className="detail-section">
                <h2>活动流程</h2>
                <div className="event-agenda">
                  {event.agenda.map((item, index) => (
                    <div key={index} className="agenda-item">
                      <span className="agenda-time">{item.time}</span>
                      <span className="agenda-content">{item.content}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* 注意事项 */}
            {event.notes && (
              <section className="detail-section">
                <h2>注意事项</h2>
                <div 
                  className="notes-content"
                  dangerouslySetInnerHTML={{ __html: event.notes }}
                />
              </section>
            )}

            {/* 活动图集 */}
            {event.images && event.images.length > 0 && (
              <section className="detail-section">
                <h2>活动图集</h2>
                <div className="event-gallery">
                  {event.images.map((img, index) => (
                    <div key={index} className="gallery-item">
                      <img 
                        src={getImageUrl(img.url || img)} 
                        alt={img.title || `图片${index + 1}`}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      {img.title && <p className="gallery-caption">{img.title}</p>}
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>

        {/* 右侧信息栏 */}
        <div className="event-sidebar">
          {/* 活动信息卡片 */}
          <div className="sidebar-card">
            <h3>活动信息</h3>
            <div className="info-list">
              {/* 时间 */}
              <div className="info-item">
                <span className="info-icon">📅</span>
                <div className="info-content">
                  <span className="info-label">活动时间</span>
                  <span className="info-value">
                    {formatDate(event.startDate)}
                    {event.startTime && (
                      <span className="info-time">
                        {formatTime(event.startTime, event.endTime)}
                      </span>
                    )}
                  </span>
                </div>
              </div>

              {/* 地点 */}
              {event.location && (
                <div className="info-item">
                  <span className="info-icon">📍</span>
                  <div className="info-content">
                    <span className="info-label">活动地点</span>
                    <span className="info-value">{event.location}</span>
                    {event.address && (
                      <span className="info-address">{event.address}</span>
                    )}
                  </div>
                </div>
              )}

              {/* 主办方 */}
              {event.organizer && (
                <div className="info-item">
                  <span className="info-icon">🏢</span>
                  <div className="info-content">
                    <span className="info-label">主办单位</span>
                    <span className="info-value">{event.organizer}</span>
                  </div>
                </div>
              )}

              {/* 联系人 */}
              {event.contact && (
                <div className="info-item">
                  <span className="info-icon">👤</span>
                  <div className="info-content">
                    <span className="info-label">联系人</span>
                    <span className="info-value">{event.contact.name || event.contact}</span>
                    {event.contact.phone && (
                      <span className="info-phone">{event.contact.phone}</span>
                    )}
                  </div>
                </div>
              )}

              {/* 费用 */}
              <div className="info-item">
                <span className="info-icon">💰</span>
                <div className="info-content">
                  <span className="info-label">活动费用</span>
                  <span className="info-value">
                    {event.fee ? `¥${event.fee}` : '免费'}
                  </span>
                </div>
              </div>

              {/* 人数限制 */}
              {event.maxParticipants && (
                <div className="info-item">
                  <span className="info-icon">👥</span>
                  <div className="info-content">
                    <span className="info-label">人数限制</span>
                    <span className="info-value">
                      {event.currentParticipants || 0}/{event.maxParticipants}人
                    </span>
                    <div className="progress-bar">
                      <div 
                        className="progress-fill"
                        style={{ 
                          width: `${((event.currentParticipants || 0) / event.maxParticipants) * 100}%` 
                        }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* 报名按钮 */}
            {canRegister ? (
              <button 
                className="register-btn primary"
                onClick={() => setShowRegistrationForm(true)}
              >
                立即报名
              </button>
            ) : (
              <button className="register-btn disabled" disabled>
                {event.status === 'ended' ? '活动已结束' : 
                 event.status === 'cancelled' ? '活动已取消' :
                 event.status === 'full' ? '活动已满员' : '暂不可报名'}
              </button>
            )}

            {/* 分享按钮 */}
            <div className="share-buttons">
              <button className="share-btn" onClick={() => navigator.clipboard.writeText(window.location.href)}>
                <i className="icon-link"></i> 复制链接
              </button>
            </div>
          </div>

          {/* 相关活动推荐 */}
          {relatedEvents && relatedEvents.length > 0 && (
            <div className="sidebar-card">
              <h3>相关活动</h3>
              <div className="related-events">
                {relatedEvents.map(relatedEvent => (
                  <Link 
                    key={relatedEvent.id} 
                    to={`/event/${relatedEvent.id}`}
                    className="related-event-item"
                  >
                    <img 
                      src={getImageUrl(relatedEvent.featuredImage) || defaultEventImg} 
                      alt={relatedEvent.title}
                    />
                    <div className="related-event-info">
                      <h4>{relatedEvent.title}</h4>
                      <span className="related-event-date">
                        {formatDate(relatedEvent.startDate)}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* 报名弹窗 */}
      {showRegistrationForm && (
        <div className="registration-modal">
          <div className="modal-backdrop" onClick={() => setShowRegistrationForm(false)} />
          <div className="modal-content">
            <div className="modal-header">
              <h3>活动报名</h3>
              <button 
                className="modal-close"
                onClick={() => setShowRegistrationForm(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <form onSubmit={(e) => { e.preventDefault(); handleRegistration(); }}>
                <div className="form-group">
                  <label>姓名 *</label>
                  <input
                    type="text"
                    value={registrationData.name}
                    onChange={(e) => setRegistrationData({...registrationData, name: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>手机号 *</label>
                  <input
                    type="tel"
                    value={registrationData.phone}
                    onChange={(e) => setRegistrationData({...registrationData, phone: e.target.value})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>邮箱</label>
                  <input
                    type="email"
                    value={registrationData.email}
                    onChange={(e) => setRegistrationData({...registrationData, email: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>参与人数 *</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={registrationData.participants}
                    onChange={(e) => setRegistrationData({...registrationData, participants: parseInt(e.target.value) || 1})}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>备注</label>
                  <textarea
                    rows="3"
                    value={registrationData.remarks}
                    onChange={(e) => setRegistrationData({...registrationData, remarks: e.target.value})}
                  />
                </div>
                <div className="form-actions">
                  <button 
                    type="button" 
                    className="btn-cancel"
                    onClick={() => setShowRegistrationForm(false)}
                  >
                    取消
                  </button>
                  <button 
                    type="submit" 
                    className="btn-submit"
                    disabled={registering}
                  >
                    {registering ? '提交中...' : '提交报名'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default EventDetail;